import { Badge } from '@/components/ui/Badge';
import type { AdminExecution } from '@/types/admin.types';
import { cn } from '@/utils/cn';
import {
  getAutomationExecutionPresentation,
  type AutomationExecutionTone,
} from '../utils/automation-execution-presentation';

interface ExecutionStatusSummaryProps {
  execution: Pick<AdminExecution, 'status' | 'details' | 'effectAlert' | 'effectOutcome'>;
  compact?: boolean;
  className?: string;
}

const panelClasses: Record<AutomationExecutionTone, string> = {
  success: 'border-success-200 bg-success-50/70 dark:border-success-500/20 dark:bg-success-500/10',
  warning: 'border-warning-200 bg-warning-50/70 dark:border-warning-500/20 dark:bg-warning-500/10',
  danger: 'border-danger-200 bg-danger-50/70 dark:border-danger-500/20 dark:bg-danger-500/10',
  info: 'border-primary-200 bg-primary-50/70 dark:border-primary-500/20 dark:bg-primary-500/10',
  neutral: 'border-neutral-200 bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800/40',
};

const categoryLabels: Record<string, string> = {
  business_prerequisite: 'Prérequis métier',
  operator_action: 'Action opérateur',
  deduplicated: 'Déduplication',
  technical_interruption: 'Technique',
};

function getFollowUpLabel(
  cancellation: ReturnType<typeof getAutomationExecutionPresentation>['cancellation'],
  unknownCancellation: boolean
): string | undefined {
  if (unknownCancellation) return 'Vérification manuelle nécessaire';
  if (!cancellation) return undefined;
  if (cancellation.requiresAttention) return 'Action requise';
  if (cancellation.retryPlanned) return 'Reprise automatique prévue';
  return 'Aucune action requise';
}

export function ExecutionStatusSummary({
  execution,
  compact = false,
  className,
}: ExecutionStatusSummaryProps) {
  const presentation = getAutomationExecutionPresentation(execution);
  const followUp = getFollowUpLabel(
    presentation.cancellation,
    presentation.unknownCancellation === true
  );
  const role =
    execution.effectAlert ||
    presentation.unknownCancellation ||
    presentation.cancellation?.requiresAttention
      ? 'alert'
      : 'status';

  if (compact || execution.status !== 'cancelled') {
    return (
      <div className={cn('flex flex-col items-start gap-1', className)} role={role}>
        <Badge variant={presentation.tone}>{presentation.label}</Badge>
        {execution.effectAlert && (
          <Badge variant="danger">
            {execution.effectOutcome === 'legacy_unknown'
              ? 'Preuve historique inconnue'
              : 'Effet non prouvé'}
          </Badge>
        )}
        {execution.status === 'cancelled' && followUp && (
          <span className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400">
            {followUp}
          </span>
        )}
      </div>
    );
  }

  return (
    <section
      className={cn('rounded-xl border p-4', panelClasses[presentation.tone], className)}
      role={role}
      aria-label="Qualification de l’annulation"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={presentation.tone}>{presentation.label}</Badge>
        {followUp && <Badge variant="neutral">{followUp}</Badge>}
        {presentation.cancellation?.retryPlanned && <Badge variant="info">Rejeu planifié</Badge>}
      </div>
      {presentation.description && (
        <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-300">
          {presentation.description}
        </p>
      )}
      {presentation.cancellation && (
        <dl className="mt-3 grid gap-2 text-xs text-neutral-500 sm:grid-cols-2 dark:text-neutral-400">
          <div>
            <dt className="font-medium text-neutral-700 dark:text-neutral-300">Déclencheur</dt>
            <dd>
              {presentation.cancellation.initiatedBy === 'administrator'
                ? 'Administrateur'
                : 'Système'}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-neutral-700 dark:text-neutral-300">Catégorie</dt>
            <dd>{categoryLabels[presentation.cancellation.category] ?? 'Catégorie à examiner'}</dd>
          </div>
        </dl>
      )}
    </section>
  );
}
